import React, { useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Tooltip,
  Switch,
  CardActions,
  Button,
  Snackbar
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import domtoimage from "dom-to-image";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Importaciones simuladas (DEBES REEMPLAZARLAS CON TUS ARCHIVOS REALES)
import menuIcon from "../../../../assets/images/icons/menu.png"; // Placeholder
import { makeRequest } from "../../../../assets/functions_helper";
import { CustomDialog, CustomDialogTitle, CustomDialogContent, CustomDialogActions } from "../../../../components/customDialog";
import { CloseButton, SendButton } from "../../../../components/buttons";

const MenuList = ({
  listOfMenus,
  showMenu,
  menuStates,
  handleSwitchDefaultMenu,
  handleDelete,
  setSelectedMenu,
  restaurantId,  // ← nueva prop
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState(null);

  // Estados para el diálogo QR
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const qrRef = useRef(null);

  // Colores unificados con el esquema de alto contraste (Blanco/Rojo)
  const COLORS = {
    ACCENT_RED: "#ff3b3b", // Rojo primario (Se usará para hover y acciones de peligro/eliminación)
    BACKGROUND_CARD: "#FFFFFF", // Fondo de tarjeta blanco
    TEXT_PRIMARY: "#333333", // Texto oscuro
    TEXT_SECONDARY: "#6A6A6A", // Texto secundario gris
    BORDER_LIGHT: "rgba(0, 0, 0, 0.08)",
    ACTIVE_GREEN: "#2ECC71", // Verde para el estado "Activo" y ahora para "Predeterminado/Favorito"
    DETAIL_BUTTON_BLUE: "#5A4E99",
  };

  const handleFavoriteToggle = (menu, isFavorite, event, isActive) => {
    event.stopPropagation();

    if (!menu.is_active && isFavorite) {
      setSnackbarMessage(
        "El menú debe estar activo para configurarse como predeterminado."
      );
      setSnackbarOpen(true);
      return;
    } else if (isActive) {
      setSnackbarMessage("El menú ya se encuentra en estado predeterminado.");
      setSnackbarOpen(true);
      return;
    }

    handleSwitchDefaultMenu(menu.id, isFavorite);
  };

  const handleToggleActivation = async (menuId, isActive, menu) => {
    if (
      "sections" in menu &&
      Array.isArray(menu.sections) &&
      menu.sections.length > 0
    ) {
      const previousActiveState = listOfMenus.find(
        (menu) => menu.id === menuId
      )?.is_active;

      console.log(isActive);

      try {
        handleSwitchDefaultMenu(menuId, isActive, true, isActive);
        await makeRequest(
          "PUT",
          `menu_manager/toggle_menu_activation/${menuId}`,
          String(isActive),
          "text/plain",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType")
        );
      } catch (error) {
        console.error("Error:", error);
        handleSwitchDefaultMenu(
          menuId,
          previousActiveState,
          true,
          previousActiveState
        );
        setSnackbarMessage("Error al actualizar el estado del menú");
        setSnackbarOpen(true);
      }
    } else {
      let errorMessage =
        "El menú debe tener por lo menos una sección y producto.";

      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
      return;
    }
  };

  const handleDeleteCloseWindow = (selectedMenuId) => {
    handleDelete(selectedMenuId);
    setDialogOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Función para abrir el diálogo QR y cargar las mesas
  const handleOpenQrDialog = async (menu) => {
    setQrDialogOpen(true);
    setLoadingTables(true);
    try {
      const response = await makeRequest(
        "GET",
        `restaurants/restaurant/info?restaurant_id=${restaurantId}`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType")
      );
      // Asumimos que response es un array de mesas con { id, number }
      setTables(response);
    } catch (error) {
      console.error("Error al cargar mesas:", error);
      setSnackbarMessage("Error al cargar las mesas");
      setSnackbarOpen(true);
    } finally {
      setLoadingTables(false);
    }
  };

  // Función para descargar el QR
  const handleDownloadQR = () => {
    if (qrRef.current) {
      domtoimage
        .toPng(qrRef.current)
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.download = `qr-mesa-${tables.name}.png`;
          link.href = dataUrl;
          link.click();
        })
        .catch((error) => {
          console.error('Error al descargar el QR:', error);
        });
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
  };

  return (
    <>
      <Box display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
        {listOfMenus.map((menu) => (
          <Card
            key={menu.id}
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            whileHover={{ scale: 1.03 }}
            sx={{
              width: 270,
              height: 380,
              backgroundColor: COLORS.BACKGROUND_CARD,
              borderRadius: "16px",
              position: "relative",
              overflow: "hidden",
              boxShadow: menuStates[menu.id]
                ? `0px 8px 20px rgba(46, 204, 113, 0.3)`
                : "0px 4px 10px rgba(0, 0, 0, 0.1)",
              border: menuStates[menu.id]
                ? `3px solid ${COLORS.ACTIVE_GREEN}`
                : `1px solid ${COLORS.BORDER_LIGHT}`,
              transition: "all 0.3s ease-in-out",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                ...(menuStates[menu.id] ? {} : {
                  border: `1px solid ${COLORS.ACCENT_RED}`,
                  boxShadow: `0px 4px 12px rgba(255, 59, 59, 0.2)`
                }),
              }
            }}
          >
            {/* Active Badge */}
            {menu.is_active && (
              <Box
                sx={{
                  position: "absolute",
                  top: 15,
                  right: 15,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: COLORS.ACTIVE_GREEN,
                  color: "white",
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px 10px",
                  borderRadius: "10px",
                  zIndex: 2,
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                ACTIVO
              </Box>
            )}

            {/* Image Section */}
            <Box
              sx={{
                width: "calc(100% - 30px)",
                height: "160px",
                overflow: "hidden",
                borderRadius: "12px",
                position: "relative",
                mt: 1.5,
              }}
            >
              <CardMedia
                component="img"
                image={menu.image || menuIcon}
                alt={menu.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: menu.is_active ? "none" : "grayscale(80%)",
                  transition: "transform 0.3s ease-in-out",
                  "&:hover": { transform: "scale(1.08)" },
                }}
              />
            </Box>

            {/* Content */}
            <CardContent
              sx={{
                textAlign: "center",
                padding: "16px 12px",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: COLORS.TEXT_PRIMARY,
                  textTransform: "capitalize",
                  fontSize: "1.1rem",
                }}
              >
                {menu.name}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: COLORS.TEXT_SECONDARY,
                  fontSize: "0.85rem",
                  flexGrow: 1,
                  maxHeight: 40,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {menu.description}
              </Typography>
            </CardContent>

            {/* Favorite & Activate Switch */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                mb: 1,
                width: "100%",
                borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
                pt: 1,
              }}
            >
              {/* Favorite Button */}
              <Tooltip title="Marcar como predeterminado" placement="bottom">
                <IconButton
                  onClick={(e) =>
                    handleFavoriteToggle(
                      menu,
                      !menuStates[menu.id],
                      e,
                      menuStates[menu.id]
                    )
                  }
                  sx={{
                    color: menuStates[menu.id] ? COLORS.ACTIVE_GREEN : COLORS.TEXT_SECONDARY,
                    transition: "color 0.3s ease-in-out",
                    "&:hover": {
                      color: menuStates[menu.id] ? COLORS.ACTIVE_GREEN : COLORS.ACCENT_RED,
                      backgroundColor: menuStates[menu.id] ? "rgba(46, 204, 113, 0.05)" : "rgba(255, 59, 59, 0.05)",
                    },
                  }}
                >
                  {menuStates[menu.id] ? (
                    <StarIcon fontSize="large" />
                  ) : (
                    <StarOutlineIcon fontSize="large" />
                  )}
                </IconButton>
              </Tooltip>

              {/* Activation Switch */}
              <Tooltip
                title={menu.is_active ? "Desactivar menú" : "Activar menú"}
                placement="bottom"
              >
                <Switch
                  checked={menu.is_active}
                  onChange={(e) =>
                    handleToggleActivation(menu.id, e.target.checked, menu)
                  }
                  color="default"
                  sx={{
                    "& .MuiSwitch-thumb": {
                      backgroundColor: menu.is_active ? COLORS.ACCENT_RED : "#ffffff",
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: menu.is_active ? `${COLORS.ACCENT_RED} !important` : `${COLORS.BORDER_LIGHT} !important`,
                      opacity: 0.5,
                    },
                  }}
                />
              </Tooltip>
            </Box>

            {/* Bottom Actions */}
            <CardActions
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                width: "100%",
                mt: "auto",
                pb: 2,
              }}
            >
              {/* Details Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="outlined"
                  onClick={() => showMenu(menu, false)}
                  sx={{
                    color: COLORS.TEXT_SECONDARY,
                    borderColor: COLORS.BORDER_LIGHT,
                    fontWeight: "600",
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: COLORS.TEXT_SECONDARY,
                      backgroundColor: "rgba(0, 0, 0, 0.04)"
                    },
                  }}
                >
                  Detalles
                </Button>
              </motion.div>

              <Box sx={{ display: "flex", gap: 0.5 }}>
                {/* QR Button */}
                <Tooltip title="QR Menu Sin Pedidos" placement="top">
                  <IconButton
                    onClick={() => handleOpenQrDialog(menu)}
                    sx={{
                      color: COLORS.TEXT_PRIMARY,
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.05)" },
                    }}
                  >
                    <QrCodeIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>

                {/* Delete Button */}
                <Tooltip title="Eliminar menú" placement="top">
                  <IconButton
                    onClick={() => {
                      setSelectedMenuId(menu.id);
                      setDialogOpen(true);
                    }}
                    sx={{
                      color: COLORS.ACCENT_RED,
                      "&:hover": { backgroundColor: "rgba(255, 59, 59, 0.1)" },
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>

                {/* Edit Styles Button */}
                <Tooltip title="Editar estilos" placement="top">
                  <IconButton
                    onClick={() => {
                      showMenu(menu, true);
                    }}
                    sx={{
                      color: COLORS.TEXT_PRIMARY,
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.05)" },
                    }}
                  >
                    <EditIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardActions>
          </Card>
        ))}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Diálogo de confirmación de eliminación */}
      <CustomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <CustomDialogTitle id="alert-dialog-title">
          Confirmar eliminación
        </CustomDialogTitle>
        <CustomDialogContent>
          ¿Estás seguro de querer eliminar el menú? Esta acción no se puede
          deshacer.
        </CustomDialogContent>
        <CustomDialogActions>
          <CloseButton onClick={() => setDialogOpen(false)} sx={{
            backgroundColor: "#E0E0E0",
            color: "#424242",
            "&:hover": {
              backgroundColor: "#D5D5D5",
            }
          }}>
            Cancelar
          </CloseButton>
          <SendButton
            onClick={() => handleDeleteCloseWindow(selectedMenuId)}
            autoFocus
          >
            Eliminar
          </SendButton>
        </CustomDialogActions>
      </CustomDialog>

      {/* Diálogo para generar QR */}
      <CustomDialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            width: '400px',
            maxWidth: '400px',
          }
        }}
      >
        <CustomDialogTitle>
          Menú Digital (Sin Pedidos)
        </CustomDialogTitle>

        <CustomDialogContent>
          {loadingTables ? (
            <Box sx={{ p: 3, height: "100px" }} />
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center">

              {/* Generamos la URL una sola vez */}
              {(() => {
                const menuUrl = `${window.location.origin}/restaurantMenuDisplay/${tables.public_id}`;

                return (
                  <>
                    {/* Sección del Enlace Copiable */}
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1, width: '100%', textAlign: 'left' }}>
                      Enlace directo al menú:
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        mb: 3,
                        p: 1,
                        pl: 1.5,
                        bgcolor: '#F5F5F5',
                        borderRadius: 2,
                        border: '1px solid #E0E0E0'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mr: 1,
                          color: '#333'
                        }}
                      >
                        {menuUrl}
                      </Typography>
                      <Tooltip title="Copiar enlace" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(menuUrl)}
                          sx={{ color: '#666', '&:hover': { color: COLORS.ACCENT_RED, bgcolor: 'rgba(247,46,65,0.08)' } }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Sección del QR */}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      O escanea este código QR:
                    </Typography>

                    <Box
                      ref={qrRef}
                      sx={{
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 3, // Un poco más redondeado para modernizar
                        display: 'inline-block',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)', // Sombra más suave y moderna
                        border: '1px solid #EDEDED',
                        mb: 2,
                      }}
                    >
                      <QRCodeCanvas
                        value={menuUrl}
                        size={200}
                        level="H"
                        includeMargin={false} // Quitamos el margin interno si ya le damos padding al Box
                      />
                    </Box>

                    <Button
                      variant="contained"
                      onClick={handleDownloadQR}
                      disableElevation
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        textTransform: 'none', // Quita el mayúsculas automático para un look más limpio
                        fontWeight: 600,
                        bgcolor: COLORS.ACCENT_RED,
                        '&:hover': { bgcolor: '#ff0000' },
                        color: 'white'
                      }}
                    >
                      Descargar QR
                    </Button>
                  </>
                );
              })()}
            </Box>
          )}
        </CustomDialogContent>

        <CustomDialogActions>
          <CloseButton onClick={() => setQrDialogOpen(false)}>Cerrar</CloseButton>
        </CustomDialogActions>
      </CustomDialog>
    </>
  );
};

export default MenuList;